import React, { useEffect, useState } from "react";
import { Sidebar, SidebarSection, SidebarSub } from "./Sidebar";
import { ContentHeader, SyncNotice, SyncWarning } from "./ContentHeader";
import { SettingsDrawer } from "./SettingsDrawer";
import { MobileShell } from "./MobileShell";
import { NavData, NavPersona, ACCENTS } from "@/lib/nav";
import { catAccentRGB } from "@/lib/format";
import { useUI } from "@/lib/theme";
import { HealthView } from "../tabs/Salud";

function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

const LS_COLLAPSED = "westive.sidebarCollapsed";
const LS_RANGE = "westive.rangeDays";

function accentForNav(section: SidebarSection, sub: SidebarSub): string {
  if (section === "coach") return ACCENTS.coach;
  if (section === "red") return ACCENTS.red;
  if (section === "calendario") return ACCENTS.calendario;
  if (section === "salud") {
    const map: Record<string, string> = {
      s_resumen: ACCENTS.saludResumen,
      s_cuerpo: ACCENTS.cuerpo,
      s_sueno: ACCENTS.sueno,
      s_cruce: ACCENTS.coach,
    };
    return map[sub] ?? ACCENTS.saludResumen;
  }
  // entrenamiento: tabs fijas tienen acento propio; disciplinas usan su color de categoría
  const fixed: Record<string, string> = {
    carga: ACCENTS.carga,
    material: ACCENTS.material,
  };
  if (fixed[sub]) return fixed[sub];
  if (!sub || sub === "resumen") return ACCENTS.brand;
  // disciplina (Carrera, Ciclismo, etc.)
  return catAccentRGB(sub);
}

function titleForNav(
  section: SidebarSection,
  sub: SidebarSub,
  t: (k: string) => string
): { breadcrumb?: string; title: string } {
  if (section === "coach") return { title: t("seg_coach") };
  if (section === "calendario") return { breadcrumb: t("nav_group_principal"), title: t("tab_calendario") };
  if (section === "red") {
    const map: Record<string, string> = {
      atletas: "coach_subtab_athletes",
      mensajes: "nav_red_messages",
      codigos: "nav_red_codes",
      entrenador: "nav_red_entrenador",
      cal_coach: "tab_calendario",
    };
    return { breadcrumb: t("nav_group_red"), title: t(map[sub] || "nav_red_athlete") };
  }
  if (section === "salud") {
    return { breadcrumb: t("seg_salud"), title: t("tab_" + (sub || "s_resumen")) };
  }
  // entrenamiento
  const FIXED = new Set(["carga", "material"]);
  const subLabel = !sub || sub === "resumen"
    ? t("tab_resumen")
    : FIXED.has(sub)
      ? t(`tab_${sub}`)
      : t(`cat_${sub}`);
  return { breadcrumb: t("nav_entrenamiento"), title: subLabel };
}

export type AppShellProps = {
  persona: NavPersona;
  navData: NavData;
  name: string;
  role: string;
  avatar?: string;
  onLogout: () => void;
  onSync: () => void;
  syncing: boolean;
  syncNotice: "rate_limited" | "timeout" | null;
  lastSyncedAt?: string;
  isAdmin: boolean;
  devImpersonating: boolean;
  devDemoAthletes: string[];
  onReload: () => void;
  readOnly: boolean;
  defaultSection?: SidebarSection;
  defaultSub?: string;
  onNavigate?: (section: SidebarSection, sub: SidebarSub) => void;
  minorityDisc?: string[];
  discCounts?: Map<string, number>;
  addedDisc?: string[];
  onToggleDisc?: (c: string) => void;
  children: (args: {
    section: SidebarSection;
    sub: SidebarSub;
    rangeDays: number | null;
    healthTab: HealthView;
    fitnessTab: string;
    onSubChange: (newSub: string) => void;
    onSectionChange: (newSection: SidebarSection, newSub?: string) => void;
    openSettings: () => void;
  }) => React.ReactNode;
};

export function AppShell({
  persona,
  navData,
  name,
  role,
  avatar,
  onLogout,
  onSync,
  syncing,
  syncNotice,
  lastSyncedAt,
  isAdmin,
  devImpersonating,
  devDemoAthletes,
  onReload,
  readOnly,
  defaultSection = "entrenamiento",
  defaultSub = "resumen",
  onNavigate,
  minorityDisc,
  discCounts,
  addedDisc,
  onToggleDisc,
  children,
}: AppShellProps) {
  const { t } = useUI();
  const isDesktop = useIsDesktop();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(LS_COLLAPSED) === "true";
    } catch {
      return false;
    }
  });

  const [section, setSection] = useState<SidebarSection>(defaultSection);
  const [sub, setSub] = useState<SidebarSub>(defaultSub);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [rangeDays, setRangeDays] = useState<number | null>(() => {
    if (readOnly) return null;
    try {
      const stored = localStorage.getItem(LS_RANGE);
      return stored === "null" || stored === null ? null : Number(stored);
    } catch {
      return null;
    }
  });

  const updateRangeDays = (v: number | null) => {
    setRangeDays(v);
    if (!readOnly) {
      try {
        localStorage.setItem(LS_RANGE, String(v));
      } catch {}
    }
  };

  const handleToggleCollapse = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(LS_COLLAPSED, String(next));
      } catch {}
      return next;
    });
  };

  const handleNavigate = (newSection: SidebarSection, newSub: SidebarSub) => {
    setSection(newSection);
    setSub(newSub);
    onNavigate?.(newSection, newSub);
  };

  const accent = accentForNav(section, sub);
  const { breadcrumb, title } = titleForNav(section, sub, t);

  const showRange =
    (section === "entrenamiento" || section === "salud") &&
    sub !== "material";

  const healthTab: HealthView = (["s_resumen", "s_cuerpo", "s_sueno", "s_cruce"].includes(sub)
    ? sub
    : "s_resumen") as HealthView;
  const fitnessTab = section === "entrenamiento" ? (sub || "resumen") : "resumen";

  // Móvil: delega al MobileShell que tiene topbar + sub-tabs + bottom-nav
  if (!isDesktop) {
    return (
      <div style={{ ["--accent" as string]: accent } as React.CSSProperties}>
        <MobileShell
          persona={persona}
          navData={navData}
          name={name}
          role={role}
          avatar={avatar}
          onLogout={onLogout}
          onSync={onSync}
          syncing={syncing}
          syncNotice={syncNotice}
          lastSyncedAt={lastSyncedAt}
          isAdmin={isAdmin}
          devImpersonating={devImpersonating}
          devDemoAthletes={devDemoAthletes}
          onReload={onReload}
          readOnly={readOnly}
          section={section}
          sub={sub}
          rangeDays={rangeDays}
          settingsOpen={settingsOpen}
          onToggleSettings={() => setSettingsOpen((o) => !o)}
          onNavigate={handleNavigate}
          onRangeChange={updateRangeDays}
        >
          {children}
        </MobileShell>
      </div>
    );
  }

  return (
    <div
      className="flex h-screen overflow-hidden"
      style={{ ["--accent" as string]: accent } as React.CSSProperties}
    >
      {/* Sidebar */}
      <Sidebar
        persona={persona}
        navData={navData}
        section={section}
        sub={sub}
        collapsed={collapsed}
        onToggleCollapse={handleToggleCollapse}
        onNavigate={handleNavigate}
        onSettingsOpen={() => setSettingsOpen(true)}
        onLogout={onLogout}
        name={name}
        role={role}
        avatar={avatar}
        minorityDisc={minorityDisc}
        discCounts={discCounts}
        addedDisc={addedDisc}
        onToggleDisc={onToggleDisc}
        onHome={() => handleNavigate("entrenamiento", "resumen")}
      />

      {/* Main area */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <ContentHeader
          title={title}
          breadcrumb={breadcrumb}
          showRange={showRange}
          rangeDays={rangeDays}
          onRangeChange={updateRangeDays}
          syncing={syncing}
          onSync={onSync}
          readOnly={readOnly}
        />

        {/* Banners (below header, outside sticky) */}
        <SyncWarning lastSyncedAt={lastSyncedAt} />
        {syncNotice && <SyncNotice type={syncNotice} />}

        {/* Content scroll area */}
        <main className="flex-1 overflow-y-auto px-6 py-6">
          {children({
          section,
          sub,
          rangeDays,
          healthTab,
          fitnessTab,
          onSubChange: (newSub) => handleNavigate(section, newSub),
          onSectionChange: (newSection, newSub = "") => handleNavigate(newSection, newSub),
          openSettings: () => setSettingsOpen(true),
        })}
        </main>
      </div>

      {/* Settings drawer */}
      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onLogout={onLogout}
        onReload={onReload}
        isAdmin={isAdmin}
        devImpersonating={devImpersonating}
        devDemoAthletes={devDemoAthletes}
      />
    </div>
  );
}
